import { useCallback, useEffect } from "react";
import { motion } from "motion/react";
import { Helmet } from "react-helmet";
import { JsonLd } from "react-schemaorg";
import Page from "../../layout/Page";
import NotFoundPage from "../NotFoundPage";
import { useStoryByPath } from "../../hooks/CmsHooks";
import { useNavigationContext } from "../../functional-components/NavigationContext";
import { Navigate, useParams } from "react-router-dom";
import ContentSection from "../../components/ContentSection";
import StoryGrid from "../../components/StoryGrid";
import CockpitEditLink from "../../components/CockpitEditLink";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import CockpitLayoutComponents from "../../components/CockpitLayoutComponents";
import { storiesBaseUrl } from "../../config/urls";
import CockpitProductGrid from "../../components/CockpitLayoutComponents/CockpitProductGrid";
import ProductLink from "../../components/ProductLink";
import Herobox from "../../components/Herobox";
import GenericOverlay from "../../components/modal/GenericOverlay";
import { unwrapAsset } from "../../utils/CockpitUtils";
import ScrollGallery from "../../components/Gallery/ScrollGallery";
import Breadcrumbs from "../../components/Breadcrumbs";
import Icon from "../../components/Icon";
import "./style.scss";
import { useLocSettings } from "../../hooks/SettingsHooks";
import { formatNumber } from "../../utils/Localization";
import { modifyMap } from "../../utils/MapUtils";

const PageWrapper = ({ displayAsOverlay = false, children }) => {
  return displayAsOverlay ? (
    <GenericOverlay noPadding id="story-overlay" key="story-overlay">
      {children}
    </GenericOverlay>
  ) : (
    children
  );
};

export const StoryPageInner = ({ story, displayAsOverlay = false }) => {
  const t = useBjelinTranslations();
  const settings = useLocSettings();

  const gotoStoryBody = useCallback(() => {
    const target = document.querySelector(".article-container");
    target.scrollIntoView({ behavior: "smooth" });
  }, []);

  if (story === null || !story.path) return null;

  const menubarOffset = displayAsOverlay ? 0 : 64;

  const storyImage = story.featured_image.styles
    ? story.featured_image.styles["px828"]
    : story.featured_image.path;

  /* Find all images in body parts:
  const images = story.parts.reduce((res, part) => {
    return part.component === "story_image"
      ? res.concat([
          {
            image: part.settings.image,
            caption: part.settings.caption,
            product: part.settings.product_link
          }
        ])
      : part.component === "image_collage"
      ? res.concat(
          part.settings.images.map(image => ({
            image: image.value,
            caption: part.settings.caption,
            product: part.settings.product_link
          }))
        )
      : res;
  }, []);
  if (story.featured_image)
    images.unshift({
      image: { ...story.featured_image },
      caption: story.caption
    });*/

  /** Handle empty assets */
  const filteredImages = story.images?.filter(item => item.value) || [];

  const images = filteredImages.map(item => unwrapAsset(item.value)) || [];
  const bodyParts = story.parts?.slice(1) || [];
  const breadcrumbItems = [{ url: storiesBaseUrl, text: t("stories.heading") }];

  return (
    <>
      <JsonLd
        item={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: story.title,
          image: storyImage,
          datePublished: story.publication_date
        }}
      />
      <Helmet>
        <meta property="og:site_name" content={"Bjelin"} />
        <meta property="og:type" content={"website"} />
        <meta property="og:title" content={story.title} />
        {story.description && (
          <meta
            property="og:description"
            name="description"
            content={story.description}
          />
        )}
        <meta property="og:image" content={storyImage} />
        {/*<meta property="fb:app_id" content={} />*/}
      </Helmet>

      <Page
        name="story-page"
        key={"story-" + story.path}
        title={story.title || "Story"}
        nonSrollable={false}
        displayAsOverlay={displayAsOverlay}
        transition={displayAsOverlay ? "simple" : undefined}
      >
        {images.length > 0 ? (
          <>
            {images && (
              <ScrollGallery images={images}>
                {!displayAsOverlay && <Breadcrumbs items={breadcrumbItems} />}
              </ScrollGallery>
            )}
            <div className="case-details">
              <div className="case-details-col1">
                <h1>{story.title}</h1>
                {(story.full_description || story.description) && (
                  <p>{story.full_description || story.description}</p>
                )}
              </div>
              <div className="case-details-col2">
                <ul>
                  {story.category && (
                    <li>
                      <strong>{t("story.category")}:</strong>{" "}
                      {t("story.category." + story.category)}
                    </li>
                  )}
                  {story.location && (
                    <li>
                      <strong>{t("story.location")}:</strong> {story.location}
                    </li>
                  )}
                  {story.year && (
                    <li>
                      <strong>{t("story.year")}:</strong> {story.year}
                    </li>
                  )}

                  {story.architect && (
                    <li>
                      <strong>{t("story.architect")}:</strong>{" "}
                      {story.architect_link ? (
                        <a
                          href={story.architect_link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {story.architect}
                        </a>
                      ) : (
                        <span>{story.architect}</span>
                      )}
                    </li>
                  )}
                  {story.builder && (
                    <li>
                      <strong>{t("story.builder")}:</strong>{" "}
                      {story.builder_link ? (
                        <a
                          href={story.builder_link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {story.builder}
                        </a>
                      ) : (
                        <span>{story.builder}</span>
                      )}
                    </li>
                  )}
                  {story.products?.length > 0 && (
                    <li>
                      <strong>{t("story.products")}:</strong>
                      <span className="product-links">
                        {story.products?.slice(0, 2).map((p, i) => (
                          <ProductLink key={i} id={p._id} />
                        ))}
                        {story.products?.length > 2 && (
                          <span> + {story.products?.length - 2}</span>
                        )}
                      </span>
                    </li>
                  )}
                  {story.area && (
                    <li>
                      <strong>{t("story.area")}:</strong>{" "}
                      {story.area && <>{formatNumber(story.area)} m²</>}
                      {settings.site === "us" && story.area_sqft && (
                        <> / {formatNumber(story.area_sqft)} sqft</>
                      )}
                    </li>
                  )}
                  {story.photographer && (
                    <li>
                      <strong>{t("story.photographer")}:</strong>{" "}
                      {story.photographer}
                    </li>
                  )}
                </ul>
              </div>
              {bodyParts.length > 0 && (
                <div className={"goto-story-container"}>
                  <motion.div
                    className="btn-goto-story"
                    onClick={() => {
                      gotoStoryBody();
                    }}
                  >
                    <Icon name={"arrow-down"} />{" "}
                    <span>{t("story.goto-article")}</span>
                  </motion.div>
                </div>
              )}
            </div>
          </>
        ) : (
          <Herobox
            {...{
              id: "",
              mode: "storytelling",
              theme: "black",
              type: "fat",
              backgroundImage: story.featured_image,
              heading: story.title,
              caption: story.caption,
              textShadow: "false"
            }}
          >
            <CockpitLayoutComponents
              components={[
                {
                  component: "bjelin_text",
                  settings: {
                    text: "<a href='/stories'>BJELIN STORIES</a>",
                    alignment: "center"
                  }
                },
                {
                  component: "bjelin_heading",
                  settings: { text: story.title, alignment: "center" }
                },
                {
                  component: "bjelin_heading",
                  settings: {
                    text: story.caption,
                    tag: "h3",
                    alignment: "center"
                  }
                },
                {
                  component: "bjelin_text",
                  settings: {
                    text:
                      story.category &&
                      story.location &&
                      story.year &&
                      `<a href="${storiesBaseUrl}/category/${
                        story.category
                      }">${t("story.category." + story.category)}</a>| ${
                        story.location
                      }, ${story.year}`,
                    alignment: "center",
                    class: "tags"
                  }
                }
              ]}
            />
          </Herobox>
        )}

        {story.parts && (
          <div className={"article-container"}>
            <CockpitLayoutComponents
              components={story.parts
                .map((part, i) => {
                  if (part.component === "story_image") {
                    if (i > 0) part.settings.menubarOffset = menubarOffset;
                    if (i === 0) part.settings.heading = story.title;
                  }
                  return part;
                })
                //hide product_grids if there are connected products (displayed below)
                .filter(part =>
                  story.products?.length > 0
                    ? !part?.children ||
                      part?.children?.findIndex(
                        ch => ch.component === "product_grid"
                      ) === -1
                    : true
                )}
            />
          </div>
        )}

        {story.products?.length > 0 && (
          <ContentSection
            padded
            className={"product-grid-section"}
            theme={"white"}
          >
            <h2 className={"text-center"}>{t("products.related")}</h2>
            <CockpitProductGrid ids={story.products.map(prod => prod._id)} />
          </ContentSection>
        )}
        <ContentSection padded className={"story-grid-section"} theme={"sand"}>
          <h2 className={"text-center"}>{t("more.stories")}</h2>
          <StoryGrid skipPath={story.path} limit={6} />
          <CockpitEditLink
            collection={"stories"}
            _id={story._id}
            labelKey={"edit.story"}
            wrapper={children => (
              <ContentSection
                containerTheme={"white"}
                className={"text-center"}
              >
                {children}
              </ContentSection>
            )}
          />
        </ContentSection>
      </Page>
    </>
  );
};

const StoryPage = ({ displayAsOverlay = false }) => {
  const params = useParams();
  const slug = params.story_slug;
  const story = useStoryByPath(slug);
  const { registerCurrentPaths, setAvailableOnSites } = useNavigationContext();
  useEffect(() => {
    registerCurrentPaths(modifyMap(story.paths, path => `/stories/${path}`));
    setAvailableOnSites(story.sites);
  }, [registerCurrentPaths, setAvailableOnSites, story.paths, story.sites]);

  if (!story.path || story.statusCode === 404) {
    return <NotFoundPage />;
  }
  if (slug !== story.path) {
    //The path used is not the correct one for the current language
    return <Navigate to={"/stories/" + story.path} replace />;
  }

  return (
    <PageWrapper displayAsOverlay={displayAsOverlay} key={slug}>
      <StoryPageInner story={story} displayAsOverlay={displayAsOverlay} />
    </PageWrapper>
  );
};

export default StoryPage;
